import { Params } from '@angular/router';
import { ID } from '@datorama/akita';
import { DateTime, Interval } from 'luxon';

export enum ISOWeekDay {
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
  Sunday = 7,
}

/**
 * This is the data we receive from the Shipping controller GetShippingType(MerchantId)
 */
export interface ShippingType {
  id: number;
  info: ShippingInfo;
  price: ShippingPrice;
}

export interface ShippingInfo {
  displayName: string;
  displayPriority: number;
  priceModel: ShippingPriceModel;
  geoAddressStart: string;
  orderDeadlineBeforeDeliveryTime: string;
  openedDays: ShippingRange[];
  closedDays: ShippingRange[];
  openDaysOfWeek: OpenWeekDay[];
  customerPackaging: boolean;
  shortTimeDelivery: boolean;
  woltShipping: boolean;
  woltUsePriceGroups: boolean;
  woltOpenDays: number;
  simpleShipping: boolean;
  kioskShipping: boolean;
  subDeliveryDays: SubDeliveryDay[];
  isDelivery: boolean;
}

export interface SubDeliveryDay {
  weekDay: ISOWeekDay;
  deadline: string; // TimeSpan
}

export interface DeliveryTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export const parseDeliveryTime = (str: string): DeliveryTime => {
  const regex = /^(\d+\.|)([0-1]\d|2[0-3]):([0-5]\d):([0-5]\d)$/g;
  const parts = regex.exec(str);
  const partArray = parts.map((part, i) => +part || 0);
  const [, days, hours, minutes, seconds] = partArray;

  return { days, hours, minutes, seconds };
};

export const rangeToDateTime = (range: ShippingRange): [DateTime, DateTime] => {
  const from = DateTime.fromISO(range.from);
  const to = DateTime.fromISO(range.to);

  return [from, to];
};

export const evaluateDateRange = (day: DateTime, ranges: ShippingRange[]): boolean => {
  for (const range of ranges) {
    const [from, to] = rangeToDateTime(range);
    if (Interval.fromDateTimes(from.startOf('day'), to.endOf('day')).contains(day)) {
      return true;
    }
  }

  return false;
};

export interface ShippingPrice {
  fixedPrice?: number;
  priceGroups: ShippingPriceGroup[];
  freeShippingExceedingTotal: number;
}

export interface ShippingPriceGroup {
  Id: ID;
  Start: number;
  End: number;
  Price: number;
}

export interface ShippingDay {
  day: ISOWeekDay;
  intervalGroups: ShippingIntervalGroup[];
}

export interface OpenWeekDay {
  day: ISOWeekDay;
}

export interface ShippingIntervalGroup {
  index: number;
  groupName: string;
  start: string;
  end: string;
  timeSlice: string;
  intervals: ShippingInterval[];
  generateIntervals: boolean;
}

export interface ShippingInterval {
  id: ID;
  index: number;
  period: string;
  disabled: boolean;
}

export interface ShippingCalculation {
  destinationAddress: string;
  shippingPrice: number;
  addressTooFarAway: boolean;
  addressNotFound: boolean;
  missingBasketTotal: number;
}

export enum ShippingPriceModel {
  fixedPrice = 0,
  priceGroups = 1,
}

export interface ShippingRange {
  id: number;
  from: string;
  to: string;
}

export interface WoltRequest extends Params {
  merchantId: string;
  street: string;
  city: string;
  postCode: string;
  deliveryTime?: string;
}

export interface WoltResponse {
  id: string;
  agreedPrice: number;
  lat: number;
  lon: number;
  agreedPickupTime: string; // ISO
  agreedTime: string; // ISO
  validUntil: string; // ISO
  error: WoltError;
}

export enum WoltError {
  None,
  InvalidAddress,
  Expired,
}
